import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultSalesTeam = () => {
  return {
    _id: '_new',
    number: '_new',
    name: '',
    commissionByDepth: [],
    distributionMode: 'AUTOMATIC',
  };
};

const getDefaultState = () => {
  return {
    salesTeams: [],
    salesTeamCount: 0,
    salesMembers: [],
    salesMemberCount: 0,
    currentUserSalesMember: null,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for a sales team
 * used for all queries/mutations which return a sales team
 * @type {DocumentNode}
 */
const SALES_TEAM_REQUEST_FIELDS = gql`
  fragment salesTeamFields on SalesTeam {
    _id
    number
    name
    commissionByDepth
    distributionMode
    memberCount
    createdAt
    updatedAt
  }
`;

/**
 * Defines the fields which are requested for a sales member
 * used for all queries/mutations which return a sales member
 * @type {DocumentNode}
 */
const SALES_MEMBER_REQUEST_FIELDS = gql`
  fragment salesMemberFields on SalesMember {
    _id
    ancestorIds
    depth
    user {
      _id
      number
      firstname
      lastname
      email
    }
    parent {
      _id
      user {
        _id
        firstname
        lastname
      }
    }
    createdAt
    updatedAt
  }
`;

/**
 * Extended fields for sales member with team data (for commission calculations)
 */
const SALES_MEMBER_WITH_TEAM_FIELDS = gql`
  fragment salesMemberWithTeamFields on SalesMember {
    _id
    depth
    team {
      _id
      name
      commissionByDepth
    }
    user {
      _id
      number
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} salesTeam
 */
const createPayload = (salesTeam) => {
  const payload = {
    name: salesTeam.name,
    commissionByDepth: salesTeam.commissionByDepth || [],
    distributionMode: salesTeam.distributionMode || 'AUTOMATIC',
  };

  console.log('Payload:', payload);

  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered sales team list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchSalesTeamsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query salesTeamsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: SalesTeamFilterInput
          ) {
            salesTeamsPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              salesTeams {
                ...salesTeamFields
              }
              salesTeamCount
            }
          }
          ${SALES_TEAM_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_SALES_TEAMS', response.data.salesTeamsPaginated.salesTeams);
      commit('SET_SALES_TEAM_COUNT', response.data.salesTeamsPaginated.salesTeamCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * Fetch a paginated, sorted and filtered sales member list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchSalesMembersPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query salesMembersPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: SalesMemberFilterInput
          ) {
            salesMembersPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              salesMembers {
                ...salesMemberFields
              }
              salesMemberCount
            }
          }
          ${SALES_MEMBER_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_SALES_MEMBERS', response.data.salesMembersPaginated.salesMembers);
      commit('SET_SALES_MEMBER_COUNT', response.data.salesMembersPaginated.salesMemberCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchSalesTeamByNumber({ commit, dispatch }, salesTeamNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query salesTeamByNumber($number: Int!) {
            salesTeamByNumber(number: $number) {
              ...salesTeamFields
            }
          }
          ${SALES_TEAM_REQUEST_FIELDS}
        `,
        variables: {
          number: parseInt(salesTeamNumber),
        },
      });
      console.log('fetchSalesTeamByNumber response:', response.data.salesTeamByNumber);
      commit('ADD_OR_UPDATE_SALES_TEAM', response.data.salesTeamByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createSalesTeam({ commit, dispatch }, salesTeam) {
    const payload = createPayload(salesTeam);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createSalesTeam($input: SalesTeamInput!) {
            createSalesTeam(input: $input) {
              ...salesTeamFields
            }
          }
          ${SALES_TEAM_REQUEST_FIELDS}
        `,
        variables: {
          input: payload,
        },
      });
      dispatch('message', {
        message: 'Verkaufsteam gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_SALES_TEAM', '_new'); // remove temporary _new sales team
      commit('ADD_OR_UPDATE_SALES_TEAM', response.data.createSalesTeam);
      return response.data.createSalesTeam.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateSalesTeam({ commit, dispatch }, salesTeam) {
    const payload = createPayload(salesTeam);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateSalesTeam($id: ID!, $input: SalesTeamUpdateInput!) {
            updateSalesTeam(id: $id, input: $input) {
              ...salesTeamFields
            }
          }
          ${SALES_TEAM_REQUEST_FIELDS}
        `,
        variables: {
          id: salesTeam._id,
          input: payload,
        },
      });
      dispatch('message', {
        message: 'Verkaufsteam aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_SALES_TEAM', response.data.updateSalesTeam);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteSalesTeam({ commit, dispatch, state }, salesTeamNumber) {
    // Look up sales team by number to get _id
    const salesTeam = state.salesTeams.find((team) => team.number == salesTeamNumber);
    if (!salesTeam || !salesTeam._id) {
      throw new Error('Sales team not found');
    }

    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation deleteSalesTeam($id: ID!) {
            deleteSalesTeam(id: $id)
          }
        `,
        variables: {
          id: salesTeam._id,
        },
      });
      dispatch('message', {
        message: 'Verkaufsteam gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_SALES_TEAM', salesTeam._id);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new sales team object in store and return it
   * @param {*} param0
   * @param {*} salesTeam
   */
  async initSalesTeam({ commit }) {
    commit('ADD_OR_UPDATE_SALES_TEAM', defaultSalesTeam());
  },

  salesTeamCleanup({ commit }) {
    commit('REMOVE_SALES_TEAM', '_new');
  },

  /**
   * Add a member to a sales team
   * @param {*} param0
   * @param {*} payload - { teamId, userId, parentId (optional) }
   */
  async addSalesMember({ commit, dispatch }, { teamId, userId, parentId }) {
    try {
      const input = {
        teamId,
        userId,
        ...(parentId && { parentId }),
      };

      const response = await apolloClient.mutate({
        mutation: gql`
          mutation addSalesMember($input: SalesMemberInput!) {
            addSalesMember(input: $input) {
              ...salesMemberFields
            }
          }
          ${SALES_MEMBER_REQUEST_FIELDS}
        `,
        variables: {
          input,
        },
      });

      dispatch('message', {
        message: 'Mitglied hinzugefügt!',
        type: 'success',
        lifetime: 2,
      });

      return response.data.addSalesMember;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Hinzufügen des Mitglieds!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async removeSalesMember({ commit, dispatch }, { memberId, mode = 'CASCADE' }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation removeSalesMember($memberId: ID!, $mode: RemoveMemberMode!) {
            removeSalesMember(memberId: $memberId, mode: $mode)
          }
        `,
        variables: {
          memberId,
          mode,
        },
      });

      dispatch('message', {
        message: 'Mitglied entfernt!',
        type: 'success',
        lifetime: 2,
      });

      return response.data.removeSalesMember;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Entfernen des Mitglieds!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async moveSalesMember({ commit, dispatch }, { memberId, newParentId }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation moveSalesMember($memberId: ID!, $newParentId: ID) {
            moveSalesMember(memberId: $memberId, newParentId: $newParentId) {
              ...salesMemberFields
            }
          }
          ${SALES_MEMBER_REQUEST_FIELDS}
        `,
        variables: {
          memberId,
          newParentId,
        },
      });

      dispatch('message', {
        message: 'Mitglied verschoben!',
        type: 'success',
        lifetime: 2,
      });

      return response.data.moveSalesMember;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Verschieben des Mitglieds!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Fetch current user's sales member data with team information
   * @param {*} param0
   * @param {*} userId
   */
  async fetchSalesMemberByUser({ commit }, userId) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query salesMemberByUser($userId: ID!) {
            salesMemberByUser(userId: $userId) {
              ...salesMemberWithTeamFields
            }
          }
          ${SALES_MEMBER_WITH_TEAM_FIELDS}
        `,
        variables: {
          userId: userId,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_CURRENT_USER_SALES_MEMBER', response.data.salesMemberByUser);
      return response.data.salesMemberByUser;
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_SALES_TEAM_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_SALES_TEAMS: (state, salesTeams) =>
    salesTeams ? (state.salesTeams = salesTeams) : (state.salesTeams = []),
  ADD_SALES_TEAM: (state, salesTeam) => state.salesTeams.push(salesTeam),
  ADD_OR_UPDATE_SALES_TEAM: (state, updatedSalesTeam) => {
    const existingSalesTeam = state.salesTeams.find(
      (salesTeam) => salesTeam._id === updatedSalesTeam._id
    );
    if (existingSalesTeam) {
      Object.assign(existingSalesTeam, updatedSalesTeam);
    } else {
      state.salesTeams.push(updatedSalesTeam);
    }
  },
  REMOVE_SALES_TEAM: (state, salesTeamId) => {
    const index = state.salesTeams.findIndex((salesTeam) => salesTeam._id === salesTeamId);
    state.salesTeams.splice(index, 1);
  },
  UPDATE_SALES_TEAM: (state, updatedSalesTeam) => {
    const salesTeam = state.salesTeams.find((salesTeam) => salesTeam._id === updatedSalesTeam._id);
    Object.assign(salesTeam, updatedSalesTeam);
  },
  SET_SALES_TEAM_COUNT: (state, salesTeamCount) => (state.salesTeamCount = salesTeamCount),
  SET_SALES_MEMBERS: (state, salesMembers) =>
    salesMembers ? (state.salesMembers = salesMembers) : (state.salesMembers = []),
  SET_SALES_MEMBER_COUNT: (state, salesMemberCount) => (state.salesMemberCount = salesMemberCount),
  SET_CURRENT_USER_SALES_MEMBER: (state, salesMember) =>
    (state.currentUserSalesMember = salesMember),
};

/**
 * Getters
 */
const getters = {
  getSalesTeams: (state) => state.salesTeams,
  getSalesTeam: (state) => (salesTeamId) =>
    state.salesTeams.find((salesTeam) => salesTeam._id == salesTeamId),
  getSalesTeamByNumber: (state) => (salesTeamNumber) =>
    state.salesTeams.find((salesTeam) => salesTeam.number == salesTeamNumber),
  getSalesTeamCount: (state) => state.salesTeamCount,
  getSalesMembers: (state) => state.salesMembers,
  getSalesMember: (state) => (salesMemberId) =>
    state.salesMembers.find((salesMember) => salesMember._id == salesMemberId),
  getSalesMemberCount: (state) => state.salesMemberCount,
  getCurrentUserSalesMember: (state) => state.currentUserSalesMember,
  getCurrentUserCommissionData: (state) => {
    const member = state.currentUserSalesMember;
    if (!member || !member.team) {
      return null;
    }
    return {
      teamName: member.team.name,
      depth: member.depth,
      level: member.depth + 1,
      commissionRate: member.team.commissionByDepth[member.depth] || 0,
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
